import CalculateIcon from "@mui/icons-material/Calculate";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import { Box, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import CustomModal from "../../components/ui/modal/modal.component";
import { Toolbar } from "../../components/ui/toolbar/toolbar.component";
import { useHasPermission } from "../../contexts/auth";
import { Action } from "../../types/toolbar";
import { PROFITABILITY_GROUP_BY_OPTIONS } from "../../consts";
import { serviceActionsSectionStyle } from "../../styles/commonStyles";
import { EditTenantSettings } from "../../workspaces/tenantConfig/editTenantSettings/editTenantSettings.component";
import { SETTINGS_SECTIONS } from "../../workspaces/tenantConfig/tenantSettings.config";
import {
  formatProfitabilityMonth,
  readProfitabilityMonth,
} from "../../workspaces/settlements/profitability/profitabilityMonth";
import { RecalculateProfitability } from "../../workspaces/settlements/profitability/recalculateProfitability.component";

const DEFAULT_GROUP_BY = PROFITABILITY_GROUP_BY_OPTIONS[0].value;

export const SettlementsProfitabilityActions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const groupByParam = params.get("group_by") || DEFAULT_GROUP_BY;
  const canViewProfitability = useHasPermission("settlement.can_view_profitability");
  const canChangeSettings = useHasPermission("config.change_tenantsettings");
  const canRecalculate = useHasPermission("settlement.can_recalculate_profitability");
  const [costSettingsOpen, setCostSettingsOpen] = useState(false);
  const [recalculateOpen, setRecalculateOpen] = useState(false);
  // The month the page shows - both read it from the URL.
  const month = formatProfitabilityMonth(readProfitabilityMonth(location.search));

  const updateGroupByParam = (value: string) => {
    const nextParams = new URLSearchParams(location.search);

    if (value === DEFAULT_GROUP_BY) {
      nextParams.delete("group_by");
    } else {
      nextParams.set("group_by", value);
    }

    const nextSearch = nextParams.toString();
    navigate({
      pathname: location.pathname,
      search: nextSearch ? `?${nextSearch}` : "",
    });
  };

  const quickFilterActions: Action[] = PROFITABILITY_GROUP_BY_OPTIONS.map((option) => ({
    icon: <FilterAltOutlinedIcon />,
    label: t(option.labelKey),
    onClick: () => updateGroupByParam(option.value),
    color: groupByParam === option.value ? "var(--primaryColor)" : undefined,
  }));

  const quickFilters = (
    <Box component="fieldset" sx={serviceActionsSectionStyle}>
      <Typography
        component="legend"
        variant="subtitle2"
        sx={{
          px: 1,
          mb: 1,
        }}
      >
        {t("Quick filters")}
      </Typography>
      <Toolbar actions={quickFilterActions} noTopPadding showShortcut={false} />
    </Box>
  );

  // The costs are workspace settings, saved through the settings endpoint -
  // so changing them takes the settings permission, as on the Workspace pages.
  const actions: Action[] = [
    {
      icon: <SettingsIcon />,
      label: t(SETTINGS_SECTIONS.profitability.editLabel),
      onClick: () => setCostSettingsOpen(true),
      disabled: !canViewProfitability || !canChangeSettings,
    },
    // Rewrites the month's frozen profitability - its own permission,
    // Admin-Finanse's by default (backend permissions_config).
    {
      icon: <CalculateIcon />,
      label: t("Recalculate profitability"),
      onClick: () => setRecalculateOpen(true),
      disabled: !canViewProfitability || !canRecalculate,
    },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%", height: "100%", minHeight: 0 }}>
      {quickFilters}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Toolbar actions={actions} noTopPadding />
      </Box>
      <CustomModal
        opened={costSettingsOpen}
        setOpened={setCostSettingsOpen}
        title={t(SETTINGS_SECTIONS.profitability.editLabel)}
        content={
          <EditTenantSettings section="profitability" onSuccess={() => setCostSettingsOpen(false)} />
        }
      />
      <CustomModal
        opened={recalculateOpen}
        setOpened={setRecalculateOpen}
        title={t("Recalculate profitability")}
        // Hugs its content - the result after recalculating is a few lines.
        minHeight={0}
        content={<RecalculateProfitability month={month} onDone={() => setRecalculateOpen(false)} />}
      />
    </Box>
  );
};
